import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, redeemCode, hasAbandonedCheckout, markCheckoutStarted } from '../api/public';

const INCLUDES = [
  '⚔️  Privacy Quest — chapter-based decision game',
  '🪞  Algorithm Mirror — filter bubble simulator',
  '🕵️  Data Heist — breach investigation challenge',
  '📊  XP tracking and personal progress dashboard',
  '🏢  Organisation leaderboards (if your org is enrolled)',
];

export default function Subscribe() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offerDiscount, setOfferDiscount] = useState(false);

  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    setOfferDiscount(hasAbandonedCheckout());
  }, []);

  async function handleSubscribe() {
    setLoading(true);
    setError(null);
    markCheckoutStarted();
    const result = await createCheckoutSession(offerDiscount);
    if (result.url) {
      window.location.href = result.url;
    } else {
      setError(result.error || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  async function handleRedeemCode() {
    if (!code.trim()) return;
    setCodeLoading(true);
    setCodeError(null);
    const result = await redeemCode(code.trim());
    if (result.success) {
      navigate('/');
    } else {
      setCodeError(result.error || 'Invalid or expired demo code.');
      setCodeLoading(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ maxWidth: 480, width: '100%', background: '#1a1a2e', borderRadius: 16, padding: '2.5rem', border: '1px solid #2d2d44', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: '1rem' }}>🔒</div>
        <h1 style={{ color: '#f8fafc', fontSize: 28, fontWeight: 700, margin: '0 0 0.5rem' }}>
          Unlock Full Access
        </h1>
        <p style={{ color: '#94a3b8', fontSize: 15, margin: '0 0 2rem' }}>
          Get unlimited access to all privacy education modules.
        </p>

        {offerDiscount && (
          <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>🎁</span>
            <div>
              <div style={{ color: '#6ee7b7', fontSize: 13, fontWeight: 700 }}>Welcome back — 10% off</div>
              <div style={{ color: '#94a3b8', fontSize: 12 }}>A discount has been applied for your first month.</div>
            </div>
          </div>
        )}

        <div style={{ background: '#0f0f1a', borderRadius: 12, padding: '1.5rem', marginBottom: '2rem', border: '1px solid #2d2d44' }}>
          {offerDiscount ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <div style={{ color: '#a78bfa', fontSize: 42, fontWeight: 800, lineHeight: 1 }}>$8.99</div>
              <div style={{ color: '#64748b', fontSize: 16, textDecoration: 'line-through' }}>$9.99</div>
            </div>
          ) : (
            <div style={{ color: '#a78bfa', fontSize: 42, fontWeight: 800, lineHeight: 1 }}>$9.99</div>
          )}
          <div style={{ color: '#64748b', fontSize: 14, marginBottom: '1.5rem' }}>per user / month{offerDiscount ? ' (first month)' : ''}</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
            {INCLUDES.map((item, i) => (
              <li key={i} style={{ color: '#cbd5e1', fontSize: 14, padding: '0.4rem 0', borderBottom: i < INCLUDES.length - 1 ? '1px solid #1e1e35' : 'none' }}>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <div style={{ background: '#450a0a', border: '1px solid #ef4444', borderRadius: 8, padding: '0.75rem', marginBottom: '1rem', color: '#fca5a5', fontSize: 14 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSubscribe}
          disabled={loading}
          style={{
            width: '100%', padding: '0.9rem', borderRadius: 10, border: 'none',
            background: loading ? '#4c1d95' : '#7c3aed', color: '#fff',
            fontSize: 16, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          {loading ? 'Redirecting to Stripe...' : offerDiscount ? 'Subscribe — $8.99 first month' : 'Subscribe — $9.99/month'}
        </button>
        <p style={{ color: '#475569', fontSize: 12, marginTop: '1rem' }}>
          Secure payment via Stripe. Cancel anytime.
        </p>

        {/* Demo code section */}
        <div style={{ marginTop: '1.5rem', borderTop: '1px solid #2d2d44', paddingTop: '1.25rem' }}>
          {!showCodeInput ? (
            <button
              onClick={() => setShowCodeInput(true)}
              style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
            >
              Have a demo code?
            </button>
          ) : (
            <div>
              <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 0.75rem' }}>Enter your demo access code:</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleRedeemCode()}
                  placeholder="Demo code"
                  style={{
                    flex: 1, padding: '0.6rem 0.75rem', borderRadius: 8,
                    border: '1px solid #3d3d5c', background: '#0f0f1a',
                    color: '#f8fafc', fontSize: 14, outline: 'none',
                  }}
                />
                <button
                  onClick={handleRedeemCode}
                  disabled={codeLoading || !code.trim()}
                  style={{
                    padding: '0.6rem 1rem', borderRadius: 8, border: 'none',
                    background: codeLoading ? '#1e3a5f' : '#1d4ed8', color: '#fff',
                    fontSize: 14, fontWeight: 600, cursor: codeLoading ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {codeLoading ? '...' : 'Redeem'}
                </button>
              </div>
              {codeError && (
                <p style={{ color: '#f87171', fontSize: 13, marginTop: '0.5rem', marginBottom: 0 }}>{codeError}</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
